import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import useNavigation from "../../../utils/useNavigation";
import service from "../../../store/services/user.service";
import clientService from "../../../store/services/clients.service";
import portfolioService from "../../../store/services/portfolio.service";
import programService from "../../../store/services/programs.service";
import Modal from "../../../components/controls/modal/Modal";
import Button from "../../../components/controls/button/Button";
import Tabs from "../../../components/controls/tabs/Tabs";
import OverviewHeader from "../../../components/overviewHeader/OverviewHeader";
import InputUnderline from "../../../components/controls/input/InputUnderline";
import Searchable from "../../../components/controls/select/CustomSelect";
import Input from "../../../components/controls/input/customInput/CustomInput";
import { CalendarIcon } from "../../../modules/default-svg";
import {
  AvatarGroup,
  AvatarGroupItem,
  AvatarGroupPopover,
  partitionAvatarGroupItems,
  Tooltip
} from "@fluentui/react-components";
import {
  dateOnlyFormatted,
  isDateGreaterThan,
  weeksBetweenDates,
} from "../../../utils/systemSettings";
import Details from "../../details/details";
import Budget from "../../budget";
import Communication from "../../communication";
import AllocatedResources from "../../allocatedResources";
import Risk from "../../risk";
import ChangeRequest from "../../changeRequest";
import ScopeItem from "../../scopeItem";
import Projects from "../../project/projects";
import { apiCall } from "../../../store/services/service";
import { URL_SEARCH_CLIENT_PORTFOLIO } from "../../../store/services/URL";
import Timeline from "../../timeline/timeline";
import { SharedTimelineContextProvider } from "../../timeline/SharedTimelineContext";
import Milestones from "../../milestone";
import DecisionLog from "../../decisionLog";
import { useDispatch } from "react-redux";
import deletionRequestService from "../../../store/services/deletionRequest.service";
import { generalActions } from "../../../store/actions";
import DeleteRequest from "../../../components/DeleteRequest/DeleteRequest";
import RichTextEditor from "../../../components/richTextEditor/RichTextEditor";
import EntityFileUpload from "../../../components/controls/fileUpload/EntityFileUpload";
import { ListChevronsDownUp, ListChevronsUpDown } from "lucide-react";
import ProgramBudget from "../../programBudget/ProgramBudget";
import ProgressTracker from "../progressTracker";
import ProgramWsrReportsView from "../wsrReports/ProgramWsrReportsView.container";
import UserSelect from "../../../components/userSelect/UserSelect";
import { UserSearchRefType } from "../../../helpers/utils";
import ResourcesReport from "../../resourcesReport/ResourcesReport";
import { ParentType } from "../../resourcesReport/constants";

const Overview = (props) => {
  const {
    programID,
    getProgramDetails,
    updateProgramsRequest,
    programData,
    programBudgetSummary,
    programProgressSummary,
    loading,
    deleteRequest,
    programOverviewLoading,
    ProgramStatusReportRequest,
    ProgramClearReportRequest,
    getProgramReport,
    getProgramReportLoading,
    ProgramConfirmWsrReport,
    getProgramConfirmedReport,
    getProgramConfirmReportLoading,
    getProgressTracker,

  } = props;
  const settings = JSON.parse(localStorage.getItem("settings"));
  const prevClientId = useRef(null);
  const manualUpdate = useRef({
    client: false,
    portfolio: false,
  });
  const [data, setData] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);

  const { updateTab, getSelectedTab } = useNavigation();

  const savedTab = getSelectedTab("Programs", programID);
  const [selectedNavItem, setSelectedNavItem] = useState(savedTab || "details");
  const [statusReportPeriod, setStatusReportPeriod] = useState("");
  const [showStatusReport, setShowStatusReport] = useState(false);
  const [pdfData, setPdfData] = useState("");
  const [reportCacheKey, setReportCacheKey] = useState(null);
  const [reportPeriod, setReportPeriod] = useState(null);
  const [showLockConfirmDialog, setShowLockConfirmDialog] = useState(false);

  const handleStatusReport = (reportOptions) => {
    ProgramStatusReportRequest(
      {
        id: programID,
        date: statusReportPeriod,
        options: reportOptions
      });
  };

  const reportModalCloseHandler = () => {
    dispatch(ProgramClearReportRequest());
    setShowStatusReport(false);
    setReportCacheKey(null);
    setReportPeriod(null);
  };

  useEffect(() => {
    if (getProgramReport) {
      if (getProgramReport.kind === "locked") {
        setPdfData(getProgramReport.url);
        setReportCacheKey(null);
        setReportPeriod(getProgramReport.period);
      } else {
        const pdfBlob = new Blob([getProgramReport.blob], { type: "application/pdf" });
        const blobUrl = URL.createObjectURL(pdfBlob);
        setPdfData(blobUrl);
        setReportCacheKey(getProgramReport.cacheKey);
        setReportPeriod(getProgramReport.period);
      }
      setShowStatusReport(true);
    }
  }, [getProgramReport, getProgramReportLoading]);

  const handleConfirmReportClick = () => {
    setShowLockConfirmDialog(true);
  };

  const handleConfirmReport = () => {
    ProgramConfirmWsrReport({ id: programID, cacheKey: reportCacheKey, period: reportPeriod });
  };

  useEffect(() => {
    if (getProgramConfirmedReport) {
      setShowLockConfirmDialog(false);
      setShowStatusReport(false);
      setReportCacheKey(null);
      setReportPeriod(null);
      getProgressTracker && getProgressTracker(programID);
    }
  }, [getProgramConfirmedReport]);

  const prevConfirmReportLoading = useRef(false);
  useEffect(() => {
    // Loading just finished without a confirmed report showing up - the confirm call
    // failed (e.g. cached preview expired). The saga already toasts the error; here we
    // just close both report modals instead of leaving them open on a dead state.
    if (prevConfirmReportLoading.current && !getProgramConfirmReportLoading && !getProgramConfirmedReport) {
      setShowLockConfirmDialog(false);
      setShowStatusReport(false);
      setReportCacheKey(null);
      setReportPeriod(null);
    }
    prevConfirmReportLoading.current = getProgramConfirmReportLoading;
  }, [getProgramConfirmReportLoading]);

  // Handle tab change - update both local state and Redux state
  const handleTabChange = (newTab) => {
    setSelectedNavItem(newTab);
    updateTab(newTab);
  };

  // Update tab state when navigation changes (e.g., back/forward navigation)
  useEffect(() => {
    const currentSavedTab = getSelectedTab("Programs", programID);
    if (currentSavedTab && currentSavedTab !== selectedNavItem) {
      setSelectedNavItem(currentSavedTab);
    }
  }, [getSelectedTab, programID, selectedNavItem]);

  const [creator, setCreator] = useState({});
  const [defClient, setDefClient] = useState(null);
  const [clientID, setClientID] = useState("");
  const [client, setClient] = useState({});
  const [clientOptions, setClientOptions] = useState([]);
  const [headerTitle, setHeaderTitle] = useState({});
  const [overviewStartDate, setOverviewStartDate] = useState("");
  const [overviewEndDate, setOverviewEndDate] = useState("");
  const [setting] = useState(settings);
  const [ProgramName, setProgramName] = useState("");
  const [budget, setBudget] = useState("");
  const [status, setStatus] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [objectives, setObjectives] = useState("");
  const [stakeholders, setStakeholders] = useState("");
  const [refId, setRefId] = useState("");
  const [manager, setManager] = useState(null);
  const [portfolioOptions, setPortfolioOptions] = useState([]);
  const [portfolio, setPortfolio] = useState("");
  const [industries, setIndustries] = useState([]);
  const [industryOptions, setIndustryOptions] = useState([]);
  const [isFullHeight, setIsFullHeight] = useState(false);
  const history = useHistory();
  const [formErrors, setFormErrors] = useState({
    name: "",
    startDate,
    endDate,
    clientId: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);

  const [isPendingDelete, setIsPendingDelete] = useState({});
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [deleteRequestModal, setDeleteRequestModal] = useState(false);
  const [deleteRequestData, setDeleteRequestData] = useState({});
  const dispatch = useDispatch();
  const [budgetSummary, setBudgetSummary] = useState({
    budget: 0,
    spent: 0,
    actual: 0,
    accural: 0,
    invoiced: 0,
    etc: 0,
    bac: 0,
  });
  const [progressSummary, setProgressSummary] = useState({
    status: 0,
    totalCount: 0,
    progressCount: 0,
    progress: 0,
  });
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(localStorage.getItem("isOverviewHeaderCollapsed") === "true");

  const handleHeaderSectionState = () => {
    setIsHeaderCollapsed(!isHeaderCollapsed);
    localStorage.setItem("isOverviewHeaderCollapsed", !isHeaderCollapsed);
  }

  const overviewHeaderFields = [
    {
      ...creator,
      label: "Created By",
      isResource: true,
    },
    {
      ...manager,
      label: "Program Manager",
      isResource: true
    },
    {
      ...client,
      label: "Client",
      title: "Client",
      image: ""
    },
    {
      label: "Start / End date",
      name: overviewStartDate || overviewEndDate
        ? `${overviewStartDate ? dateOnlyFormatted(setting, overviewStartDate) : "-"} to ${overviewEndDate ? dateOnlyFormatted(setting, overviewEndDate) : "-"}`
        : "-",
      secondary: overviewStartDate && overviewEndDate
        ? weeksBetweenDates(overviewStartDate, overviewEndDate)
        : null,
    },
  ];

  const parentData = { id: programID, type: 2 };
  const renderSelectedComponent = () => {
    switch (selectedNavItem) {
      case "details":
        return <Details data={programData} type="Program" isHeaderCollapsed={isHeaderCollapsed} />;
      case "healthStatus":
        return <ProgressTracker
          programId={programID}
          handleUpdatePeriod={(date) => setStatusReportPeriod(date)}
        />;
      case "wsrReports":
        return <ProgramWsrReportsView refId={programID} />;
      case "projects":
        return (
          <Projects
            programID={programID}
            defaultClient={defClient}
            defaultPortfolio={{
              id: programData?.portfolio?.id,
              name: programData?.portfolio?.name,
            }}
            defaultProgram={{ id: programData?.id, name: programData?.name }}
            overviewType="Program"
          />
        );
      case "scopeItem":
        return (
          <ScopeItem
            programID={programID}
            defaultClient={programData}
            overviewType="Program"
          />
        );
      case "milestones":
        return (
          <Milestones
            parentData={{id: programData.id, name: programData.name}}
            parentType="Program"
          />
        );
      case "changeRequest":
        return (
          <ChangeRequest
            programID={programID}
            defaultClient={programData}
            overviewType="Program"
          />
        );
      case "risk/Issues":
        return (
          <Risk
            programID={programID}
            defaultClient={programData}
            overviewType="Program"
          />
        );
      case "allocatedResources":
        return <ResourcesReport 
                  parentType={ParentType.PROGRAM} 
                  parentId={programID}
                  metaData={{ client: { id: client?.id, name: client?.name } }}/>;
      case "communication":
        return (
          <Communication
            programID={programID}
            defaultProgram={programData}
            overviewType="Program"
          />
        );
      case "budget":
        return (
          <ProgramBudget
            programID={programID}
            defaultProgram={programData}
            overviewType="Program"
          />
        );
      case "decisionLog":
        return (
          <DecisionLog
            programID={programID}
            defaultProgram={programData}
            overviewType="Program"
          />
        );
      case "timeline":
        return (
          <SharedTimelineContextProvider>
            <Timeline
              entityID={programID}
              isFullHeight={isHeaderCollapsed}
              entityType="program"
              setFullHeight={setIsFullHeight}
            />
          </SharedTimelineContextProvider>
        );
      default:
        return <Details data={programData} type="Program" isHeaderCollapsed={isHeaderCollapsed} />;
    }
  };

  const formBody = () => {
    const { inlineItems, overflowItems } = partitionAvatarGroupItems({
      // items: assignedResources,
      items: [],
    });
    return (
      <div>
        <InputUnderline
          placeholder="Add program name"
          value={ProgramName}
          error={formErrors.name}
          handleChange={setProgramName}
          isRequired
          loading={isLoading}
        />
        <div className="d-flex mb-3">
          <div className="mt-4 w-50">
            <Searchable
              label="Client"
              CustomClass="ModalAvatarSearch"
              selectWd="me-3"
              persona={true}
              searchText={searchClientHandler}
              SelectText="client"
              options={clientOptions}
              defaultVal={clientID}
              onChange={(e) => {
                setClientID(e);
                setManager(null);
                setPortfolio("");
                setIndustries([]);
              }}
              errorMessage={formErrors.clientId}
              isRequired
              loading={isLoading}
            />
          </div>
          <div className="mt-4 w-50">
            <Searchable
              label="Portfolio"
              CustomClass="ModalAvatarSearch"
              persona={true}
              searchText={searchPortfolioHandler}
              SelectText="portfolio"
              options={portfolioOptions}
              defaultVal={portfolio}
              onChange={(e) => setPortfolio(e)}
              loading={isLoading}
            />
          </div>
        </div>
        <div className="ModalAvatarGroup">
          <div className="row align-items-end gx-3">
            <div className="col-4">
              <UserSelect
                refType={UserSearchRefType.Client}
                refId={clientID?.value}
                label="Program Manager"
                buttonLabel={"Select Manager"}
                value={manager}
                onChange={setManager}
                loading={isLoading}
                disabled={!clientID}
              />
            </div>
            <div className="col-4">
              <Searchable
                label="Industry"
                SelectText="industry"
                multiselect={true}
                creatable={false}
                searchText={handleIndustrySearch}
                options={industryOptions}
                defaultVal={industries.map((i) => ({ label: i.name, value: i.id }))}
                onChange={handleIndustriesChange}
                isDisabled={!clientID?.value}
                loading={isLoading}
              />
            </div>
            <div className="col-8">
              {inlineItems && !isLoading && (
                <div className="AvatarGroupWrapper">
                  <AvatarGroup layout="stack">
                    {inlineItems.map((item) => (
                      <AvatarGroupItem
                        size={28}
                        name={item.label}
                        image={{ src: item.image ? item.image : "" }}
                        key={item.name}
                      />
                    ))}
                    {overflowItems && (
                      <AvatarGroupPopover>
                        {overflowItems.map((item) => (
                          <AvatarGroupItem
                            name={item.label}
                            image={{ src: item.image ? item.image : "" }}
                            key={item.name}
                          />
                        ))}
                      </AvatarGroupPopover>
                    )}
                  </AvatarGroup>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="row align-items-center gx-3 mb-3">
          <div className="col-4">
            <Input
              icon={false}
              value={refId}
              type="text"
              placeholder="Enter reference ID"
              handleChange={setRefId}
              label="Reference ID"
              loading={isLoading}
            />
          </div>
          <div className="col-4">
            <Input
              icon={<CalendarIcon />}
              value={startDate}
              maxDate={endDate}
              type="date"
              handleChange={setStartDate}
              label="Start Date"
              errorMessage={formErrors.startDate}
              loading={isLoading}
            />
          </div>
          <div className="col-4">
            <Input
              icon={<CalendarIcon />}
              value={endDate}
              minDate={startDate}
              type="date"
              handleChange={setEndDate}
              label="End Date"
              errorMessage={formErrors.endDate}
              loading={isLoading}
            />
          </div>
        </div>
        <div className="row align-items-center gx-3 mb-3">
          <div className="col-4">
            <Input
              icon={false}
              placeholder="Enter status"
              value={status}
              type="tesxt"
              handleChange={setStatus}
              label="Status"
              loading={isLoading}
            />
          </div>
          <div className="col-4">
            <Input
              icon={false}
              type="number"
              currency={true}
              placeholder="0"
              value={budget}
              handleChange={setBudget}
              label="Budget"
              loading={isLoading}
            />
          </div>
        </div>
        <div className="mb-3">
          <RichTextEditor
            label="Objectives"
            content={objectives}
            onChange={setObjectives}
            loading={isLoading}
          />
        </div>
        <div className="mb-3">
          <RichTextEditor
            label="Stakeholders"
            content={stakeholders}
            onChange={setStakeholders}
            loading={isLoading}
          />
        </div>

        <EntityFileUpload
          entityId={programID}
          entityType="Program"
          attachments={attachments}
          onAttachmentsChange={() => {
            // Refresh program data to get updated attachments
            if (programID) {
              service.fetchProgramDataRequest({ id: programID })
                .then((programData) => {
                  setAttachments(programData.attachments);
                })
                .catch((error) => {
                  console.error("Error fetching program data:", error);
                });
            }
          }}
          disabled={isLoading || !programID}
        />
      </div>
    );
  };

  const showDeleteModal = () => {
    setDeleteModal(true);
  };

  const showEditModal = async () => {
    setIsEdit(true);
  };

  const searchClientHandler = (value) => {
    if (value === "") {
      setClientOptions([]);
      return;
    }
    clientService
      .searchClientsRequest(value)
      .then((res) => {
        const transformedArray = res.map((obj) => ({
          label: obj.name,
          value: obj.id,
        }));
        setClientOptions(transformedArray);
      })
      .catch(() => {
        setClientOptions([]);
      });
  };

  const searchPortfolioHandler = (value) => {
    if (value === "") {
      setPortfolioOptions([]);
      return;
    }
    const payload = { id: clientID?.value, searchText: value };
    let queryParam = `?searchTerm=${payload.searchText}`;
    if (clientID)
      queryParam = `?clientId=${payload.id}&searchTerm=${payload.searchText}`;
    apiCall
      .get({
        url: `${URL_SEARCH_CLIENT_PORTFOLIO}${queryParam}`,
        isAuthToken: true,
      })
      .then((res) => {
        const transformedArray = res.map((obj) => ({
          label: obj.name,
          value: obj.id,
        }));
        setPortfolioOptions(transformedArray);
      })
      .catch(() => {
        setPortfolioOptions([]);
      });
  };

  // Fetches at most once per selected client - CustomSelect's onFocus can fire
  // more than once per interaction (it programmatically calls openMenu()), and
  // react-select's onInputChange also fires on blur/menu-close, so this cache
  // is the actual guard against repeat calls, not the actionMeta check below.
  const fetchedIndustriesClientIdRef = useRef(null);
  const fetchClientIndustries = (clientId) => {
    if (!clientId) {
      fetchedIndustriesClientIdRef.current = null;
      setIndustryOptions([]);
      return;
    }
    if (fetchedIndustriesClientIdRef.current === clientId) {
      return;
    }
    fetchedIndustriesClientIdRef.current = clientId;
    clientService
      .getClientIndustriesRequest(clientId)
      .then((res) => {
        const transformed = (res || []).map((obj) => ({ label: obj.name, value: obj.id }));
        setIndustryOptions(transformed);
      })
      .catch(() => {
        fetchedIndustriesClientIdRef.current = null;
        setIndustryOptions([]);
      });
  };

  const handleIndustrySearch = (value, actionMeta) => {
    if (value !== null && actionMeta && actionMeta.action !== "input-change") {
      return;
    }
    fetchClientIndustries(clientID?.value);
  };

  const handleIndustriesChange = (ids) => {
    const pool = [...industries, ...industryOptions.map((o) => ({ id: o.value, name: o.label }))];
    const selected = ids.map((id) => pool.find((item) => item.id === id)).filter(Boolean);
    setIndustries(selected);
  };

  const handleSave = () => {
    const errors = {};
    if (!ProgramName) errors["name"] = `Name Field is Required.`;
    if (!clientID) errors["clientId"] = `Client Field is Required.`;
    if (startDate && endDate && isDateGreaterThan(startDate, endDate)) {
      errors[
        "endDate"
      ] = `End date must be equal to or greater than start date.`;
    }
    setFormErrors(errors);
    if (errors && Object.keys(errors).length > 0) {
      return;
    }
    const payload = {
      budget: budget || 0,
      clientId: clientID?.value,
      endDate: endDate || null,
      name: ProgramName,
      objectives: objectives,
      ownerId: manager?.id,
      portfolioId: portfolio?.value,
      refId: refId,
      stakeholderList: stakeholders,
      startDate: startDate || null,
      status: status,
      industryIds: industries.map((i) => i.id),
    };
    updateProgramsRequest({ id: data.id, type: "overview", payload });
  };

  useEffect(() => {
    if (!loading) {
      setIsEdit(false);
    }
  }, [loading]);

  const handleCancel = () => {
    setFormErrors({});
    setDeleteModal(false);
    setIsEdit(false);
  };

  const handleDelete = () => {
    setIsDeleteLoading(true);
    if (deleteRequest) {
      deletionRequestService
        .confirmDeletionRequest(deleteRequest.requestId)
        .then((response) => {
          setIsDeleteLoading(false);
          setDeleteModal(false);
          history.push(`/program`);
        })
        .catch((err) => {
          dispatch(
            generalActions.showToast({
              title: "Something went wrong. Please try again later.",
              type: "error",
            })
          );
          setIsDeleteLoading(true);
          setDeleteModal(false);
        });
    } else {
      programService
        .deleteProgramsRequest(programID)
        .then((response) => {
          setIsDeleteLoading(false);
          setDeleteModal(false);
          if (response && !response.IsDeletionAllowed) {
            setDeleteRequestData(response);
            setDeleteRequestModal(true);
          } else {
            history.goBack();
          }
        })
        .catch((err) => {
          dispatch(
            generalActions.showToast({
              title: "Something went wrong. Please try again later.",
              type: "error",
            })
          );
          setIsDeleteLoading(true);
          setDeleteModal(false);
        });
    }
  };

  const handleDeleteRequested = () => {
    getProgramDetails({ id: programID });
  };

  useEffect(() => {
    if (programData.hasOwnProperty("name")) {
      setCreator(programData.creator);
      setClient(programData.client);
      setManager(programData.owner);
      setOverviewStartDate(programData.startDate);
      setOverviewEndDate(programData.endDate);
      setHeaderTitle(programData);
      setIsPendingDelete(programData.isPendingDelete);
      setData(programData);
      setDefClient(programData.client);
      programData.client
        ? setClientID({
          value: programData.client.id,
          label: programData.client.name,
        })
        : setClientID("");
    }
    setBudgetSummary({
      budget: programData.budget,
      spent: programData.spent,
      actual: programData.actual,
      accural: programData.accural,
      invoiced: programData.invoiced,
      etc: programData.etc,
      bac: programData.bac,
    });
    setProgressSummary(programData.progressSummary);
  }, [programData]);

  useEffect(() => {
    if (programBudgetSummary) {
      setBudgetSummary(programBudgetSummary);
    }
  }, [programBudgetSummary]);

  useEffect(() => {
    if (programProgressSummary) {
      setProgressSummary(programProgressSummary);
    }
  }, [programProgressSummary]);

  const fetchProgramData = async () => {
    setIsLoading(true);
    programService
      .fetchProgramDataRequest({ id: programData.id })
      .then((data) => {
        setProgramName(data.name);
        setBudget(data.budget);
        setStartDate(data.startDate);
        setEndDate(data.endDate);
        setStatus(data.status);
        setObjectives(data.objectives);
        setStakeholders(data.stakeholderList);
        setRefId(data.refId);
        setAttachments(data.attachments || []);
        setPortfolio(data.portfolio);
        setManager(data.owner);
        setIndustries(data.industries || []);
        if (data.portfolio) {
          const manArr = {
            value: data.portfolio.id,
            label: data.portfolio.name,
          };
          setPortfolio(manArr);
        }
        data.client
          ? setClientID({ value: data.client.id, label: data.client.name })
          : setClientID("");
        setIsLoading(false);
      })
      .catch((error) => console.error("Error fetching data:", error));
  };

  useEffect(() => {
    if (isEdit) {
      fetchProgramData();
    }
  }, [isEdit]);

  useEffect(() => {
    getProgramDetails({ id: programID });
  }, []);

  useEffect(() => {
    if (portfolio && !manualUpdate.current.client) {
      if (!clientID) {
        portfolioService
          .getPortfolioDetails({ id: portfolio.value })
          .then((portfolioData) => {
            manualUpdate.current.client = true;
            portfolioData.client
              ? setClientID({
                label: portfolioData.client.name,
                value: portfolioData.client.id,
              })
              : setClientID("");
          })
          .catch((error) =>
            console.error("Error fetching portfolio data:", error)
          );
      }
    }
  }, [portfolio]);

  useEffect(() => {
    if (!clientID) {
      setPortfolio("");
    }
    if (clientID) {
      prevClientId.current = clientID.value;
    }
    // if (ClientId && prevClientId.current === null) {
    //   prevClientId.current = ClientId.value;
    // }
    if (clientID && prevClientId.current !== clientID.value) {
      setPortfolio("");
      prevClientId.current = clientID.value;
    }
    manualUpdate.current.client = false;
    manualUpdate.current.portfolio = false;
  }, [clientID]);

  useEffect(() => {
    if (deleteRequest) {
      setDeleteModal(true);
    }
  }, [deleteRequest]);

  return (
    <div className="relative">
      {!isFullHeight && !isHeaderCollapsed && (
        <OverviewHeader
          entityType="program"
          headerTitle={headerTitle}
          headerFields={overviewHeaderFields}
          parentData={parentData}
          showDeleteModal={showDeleteModal}
          showEditModal={showEditModal}
          isShowAction
          loading={programOverviewLoading}
          isPendingDelete={isPendingDelete}
          handleRefresh={() => handleDeleteRequested()}
          budgetSummary={budgetSummary}
          progressSummary={progressSummary}
          handleStatusReport={handleStatusReport}
          getReportLoading={getProgramReportLoading}
        />
      )}
      {!isFullHeight && (
        <div className={isHeaderCollapsed ? "pt-3" : ''}>
          <Tabs
            selectedTab={selectedNavItem}
            setSelectedTab={handleTabChange}
            type="program"
            programID={programID}
          />
        </div>
      )}
      {deleteModal && (
        <Modal
          title={`Delete program`}
          body={
            <p className="text-sm-400 subHeading-color">{`Are you sure you want to delete the program?`}</p>
          }
          handleCancel={() => handleCancel()}
          handleSave={() => handleDelete()}
          saveBtnLabel="Delete"
          cancelBtnLabel="Cancel"
          isLoading={isDeleteLoading}
        />
      )}
      {deleteRequestModal && (
        <DeleteRequest
          data={deleteRequestData}
          handleCancel={setDeleteRequestModal}
          handleRefresh={() => handleDeleteRequested()}
        />
      )}
      {isEdit && (
        <Modal
          title={`Edit ${programData.name}`}
          handleCancel={() => handleCancel()}
          handleSave={() => handleSave()}
          saveBtnLabel="Save"
          cancelBtnLabel="Cancel"
          isLoading={loading}
          size="large"
          body={formBody()}
        />
      )}
      <section
        className="TabsContentSection"
        style={{
          height: `calc(100vh - ${isHeaderCollapsed ? '205' : '400'}px)`,
        }}>
        <div key={selectedNavItem} className="TabsContentFade">
          {renderSelectedComponent()}
        </div>
      </section>
      {showStatusReport && (
        <Modal
          htmlTitle={
            <div className="d-flex justify-content-between align-items-center w-100 px-3">
              <span>{`${data.name} - Weekly Status Report`}</span>
              {reportCacheKey && (
                <Button
                  label="Confirm & Lock Period"
                  btnClass="d_btn d_btn_primary"
                  showLoading={getProgramConfirmReportLoading}
                  isDisabled={getProgramConfirmReportLoading}
                  clickHandler={handleConfirmReportClick}
                />
              )}
            </div>
          }
          handleCancel={reportModalCloseHandler}
          body={
            pdfData && (
              <iframe
                title="PDF Viewer"
                src={pdfData}
                width="100%"
                height="100%"
              ></iframe>
            )
          }
          type={true}
          footer={false}
          size=""
        />
      )}
      {showLockConfirmDialog && (
        <Modal
          title="Confirm report"
          body={
            <p>
              Confirming will lock this period - Health KPIs and progress
              data can no longer be edited until reverted. Continue?
            </p>
          }
          handleCancel={() => setShowLockConfirmDialog(false)}
          handleSave={handleConfirmReport}
          saveBtnLabel="Confirm"
          cancelBtnLabel="Cancel"
          isLoading={getProgramConfirmReportLoading}
        />
      )}
      {!programOverviewLoading &&
        (
          <div
            className="absolute top-2 right-2 cursor-pointer opacity-50 hover:opacity-100 hover:text-[#342C82] px-3 py-1 transition-all duration-150 ease-in-out"
            onClick={handleHeaderSectionState}>
            <Tooltip content={isHeaderCollapsed ? "Expand" : "Collapse"} relationship="label" positioning="before">
              {isHeaderCollapsed ? <ListChevronsUpDown size={20} /> : <ListChevronsDownUp size={20} />}
            </Tooltip>
          </div>
        )}
    </div>
  );
};
export default Overview;
